import { can, etbToSantim, type AdminUserDetailDto } from '@bravo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  Badge,
  Button,
  Card,
  ErrorBlock,
  Field,
  PageHeader,
  Spinner,
  TextArea,
  TextInput,
  statusTone,
} from '../components/ui';
import { apiData } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';
import { useSession } from '../lib/session';

type Tab = 'overview' | 'bets' | 'ledger' | 'money';

/** Adjustments at or above this (santim) require typing the amount. 10,000 ETB. */
const LARGE_ADJUSTMENT = 1_000_000;

export function UserDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const format = useFormat();
  const session = useSession();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [sign, setSign] = useState<'credit' | 'debit'>('credit');
  const [confirmAdjust, setConfirmAdjust] = useState(false);
  const [busy, setBusy] = useState(false);

  const query = useQuery({
    queryKey: ['user', id],
    queryFn: () => apiData<AdminUserDetailDto>(`/admin/users/${id}`),
  });

  if (query.isLoading) return <Spinner />;
  if (query.isError || !query.data) {
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} />;
  }
  const { user, bets, ledger, topUps, withdrawals } = query.data;
  const role = session.data?.role;
  const canSuspend = role ? can(role, 'users.suspend') : false;
  const canAdjust = role ? can(role, 'users.adjust') : false;

  async function setStatus(next: 'suspended' | 'active') {
    setBusy(true);
    try {
      await apiData(`/admin/users/${id}/${next === 'suspended' ? 'suspend' : 'unsuspend'}`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
      toastOk(t('saved'));
      setReason('');
      await query.refetch();
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  async function adjust() {
    const parsed = etbToSantim(amount);
    const signed = sign === 'debit' ? -parsed : parsed;
    setBusy(true);
    try {
      await apiData(`/admin/users/${id}/adjustments`, {
        method: 'POST',
        body: { amount: signed, reason: reason.trim() },
      });
      toastOk(t('users.adjusted'));
      setConfirmAdjust(false);
      setAmount('');
      setReason('');
      await query.refetch();
      await queryClient.invalidateQueries({ queryKey: ['stats'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  const parsedAmount = (() => {
    try {
      return amount.trim() ? etbToSantim(amount) : 0;
    } catch {
      return 0;
    }
  })();
  const large = parsedAmount >= LARGE_ADJUSTMENT;

  return (
    <div>
      <PageHeader
        title={user.displayName || user.email || user.phone || user.id}
        subtitle={user.email || user.phone}
        actions={
          <Link to="/users" className="text-sm font-semibold text-brand-text">
            {t('back')}
          </Link>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Badge tone={statusTone(user.status)}>{format.status(user.status)}</Badge>
        <Badge>
          {format.etb(user.balanceAvailable)}
          {user.balanceHeld > 0 ? ` · ${t('users.held')} ${format.etb(user.balanceHeld)}` : ''}
        </Badge>
      </div>
      <div className="mb-4 flex gap-2">
        {(['overview', 'bets', 'ledger', 'money'] as const).map((item) => (
          <Button
            key={item}
            variant={tab === item ? 'primary' : 'secondary'}
            onClick={() => setTab(item)}
          >
            {t(`users.tabs.${item}`)}
          </Button>
        ))}
      </div>

      {tab === 'overview' ? (
        <Card className="space-y-4">
          <p className="text-sm text-fg-muted">
            {t('users.kyc')}: {user.kyc?.fullName || '—'} · {user.kyc?.idNumber || '—'}
          </p>
          {canSuspend ? (
            <div className="space-y-3">
              <Field label={t('reason')}>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} />
              </Field>
              {user.status === 'active' ? (
                <Button
                  variant="danger"
                  loading={busy}
                  disabled={reason.trim().length < 3}
                  onClick={() => void setStatus('suspended')}
                >
                  {t('users.suspend')}
                </Button>
              ) : (
                <Button
                  loading={busy}
                  disabled={reason.trim().length < 3}
                  onClick={() => void setStatus('active')}
                >
                  {t('users.unsuspend')}
                </Button>
              )}
            </div>
          ) : null}
          {canAdjust ? (
            <div className="space-y-3 border-t border-border pt-4">
              <h2 className="font-semibold">{t('users.adjust')}</h2>
              <div className="flex gap-2">
                <Button
                  variant={sign === 'credit' ? 'primary' : 'secondary'}
                  onClick={() => setSign('credit')}
                >
                  {t('users.credit')}
                </Button>
                <Button
                  variant={sign === 'debit' ? 'primary' : 'secondary'}
                  onClick={() => setSign('debit')}
                >
                  {t('users.debit')}
                </Button>
              </div>
              <Field label={t('amount')} hint={t('users.etbHint')}>
                <TextInput
                  inputMode="decimal"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </Field>
              <Button
                disabled={parsedAmount <= 0 || reason.trim().length < 3}
                onClick={() => (large ? setConfirmAdjust(true) : void adjust())}
              >
                {t('users.adjust')}
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {tab === 'bets' ? (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
          {bets.length === 0 ? <li className="p-4 text-sm text-fg-muted">{t('empty')}</li> : null}
          {bets.map((bet) => (
            <li key={bet.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span>{format.text(bet.marketTitle)}</span>
              <span>{format.etb(bet.stake)}</span>
              <Badge tone={statusTone(bet.status)}>{format.status(bet.status)}</Badge>
            </li>
          ))}
        </ul>
      ) : null}

      {tab === 'ledger' ? (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
          {ledger.length === 0 ? <li className="p-4 text-sm text-fg-muted">{t('empty')}</li> : null}
          {ledger.map((entry) => (
            <li key={entry.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <span>{entry.type}</span>
              <span>{format.etb(entry.availableDelta)}</span>
              <span className="text-fg-muted">{format.when(entry.createdAt)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {tab === 'money' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <h2 className="mb-2 font-semibold">{t('nav.topups')}</h2>
            {topUps.map((row) => (
              <p key={row.id} className="text-sm">
                {format.etb(row.amountClaimed)} · {format.status(row.status)}
              </p>
            ))}
          </Card>
          <Card>
            <h2 className="mb-2 font-semibold">{t('nav.withdrawals')}</h2>
            {withdrawals.map((row) => (
              <p key={row.id} className="text-sm">
                {format.etb(row.amount)} · {format.status(row.status)}
              </p>
            ))}
          </Card>
        </div>
      ) : null}

      {confirmAdjust ? (
        <ConfirmDialog
          title={t('users.adjust')}
          body={t('users.adjustBody')}
          phrase={amount.trim()}
          confirmLabel={t('users.adjust')}
          danger={sign === 'debit'}
          busy={busy}
          onClose={() => setConfirmAdjust(false)}
          onConfirm={() => void adjust()}
        />
      ) : null}
    </div>
  );
}
